/**
 * The DQ Token section of an invoice — shared by the invoice dialog and its PDF, so
 * both always say the same thing.
 *
 * `invoice.dqt` comes from Billing history: amounts are pre-formatted by the API, so
 * no currency's minor unit has to be known here.
 */

export const isDqtInvoice = (invoice) => invoice?.invoice_type === 'dqt' && !!invoice?.dqt;

/**
 * The charges of an invoice, one row each (20 Oct 2026): a partner's sign-up (the plan
 * once + its year of access), a direct order (setup, licences, credits) or a yearly
 * renewal. [] when the invoice is a single charge — its description says it all.
 *
 * @returns {{ key, description, type: 'One-time' | 'Yearly', amount }[]}
 */
export const invoiceCharges = (invoice) => (invoice?.dqt?.lines || invoice?.units?.lines || []).map((l) => ({
    key: l.key,
    description: l.description,
    type: l.billing === 'annual' ? 'Yearly' : 'One-time',
    amount: l.amount_display,
}));

/** The heading of the DQT section: a yearly renewal buys no DQ Tokens. */
export const dqtSectionTitle = (dqt) => (dqt?.kind === 'annual_access' ? 'Payment' : 'DQ Tokens');

/** [label, value] pairs, in display order. With a Charges table, no description row. */
export const dqtInvoiceRows = (dqt) => {
    if (!dqt) return [];
    const paidInUsd = !dqt.paid_currency || dqt.paid_currency === 'USD';
    // A yearly renewal (kind 'annual_access') is a fee: no DQ Tokens rows.
    const buysDqt = dqt.kind !== 'annual_access';
    return [
        ...((dqt.lines || []).length ? [] : [['Description', dqt.description]]),
        ['Amount paid', dqt.paid_display],
        ...(paidInUsd ? [] : [['USD equivalent', dqt.usd_display]]),
        ...(buysDqt ? [
            ['DQ Tokens received', `${Number(dqt.dqt_amount || 0).toLocaleString('en-US')} DQT`],
            ['Rate', `1 DQT = US$${dqt.usd_per_dqt}`],
        ] : []),
        ...(dqt.payment_reference ? [['Payment reference', dqt.payment_reference]] : []),
    ].filter(([, value]) => value !== null && value !== undefined && value !== '');
};

/**
 * A direct workspace's invoice (invoice_type 'direct') — a school or network buying for
 * itself is shown licences and credits, never DQ Tokens (23 Sep 2026). `invoice.units`
 * comes from Billing history.
 */
export const isDirectInvoice = (invoice) => invoice?.invoice_type === 'direct' && !!invoice?.units;

/** [label, value] pairs for a direct invoice, in display order. */
export const directInvoiceRows = (units) => {
    if (!units) return [];
    const paidInUsd = !units.paid_currency || units.paid_currency === 'USD';
    const fmt = (n) => Number(n).toLocaleString('en-US');
    return [
        ...((units.lines || []).length ? [] : [['Description', units.description]]),
        ['Amount paid', units.paid_display],
        ...(paidInUsd ? [] : [['USD equivalent', units.usd_display]]),
        ...(units.licences ? [['Licenses received', fmt(units.licences)]] : []),
        ...(units.credits !== null && units.credits !== undefined
            ? [['Credits received', fmt(units.credits)]]
            : (units.licences ? [] : [['Credits received', 'Being added to your balance']])),
    ].filter(([, value]) => value !== null && value !== undefined && value !== '');
};

/** One line on how a direct payment was made. */
export const directPaymentNote = (units) => {
    if (!units) return '';
    if (!units.paid_currency || units.paid_currency === 'USD') return 'Paid through Stripe in US dollars.';
    return `Paid through Stripe in ${units.paid_currency}; the price is set in US dollars and Stripe converted it at checkout.`;
};

/** One line on how the payment was made. */
export const dqtPaymentNote = (dqt) => {
    if (!dqt) return '';
    if (dqt.kind === 'offline_grant') return 'Paid outside Stripe and recorded by DQ against the payment reference above.';
    if (dqt.kind === 'annual_access') return 'Renewal of your annual solution access, paid on your Stripe invoice.';
    if (!dqt.paid_currency || dqt.paid_currency === 'USD') return 'Paid through Stripe in US dollars.';
    return `Paid through Stripe in ${dqt.paid_currency}; the price is set in US dollars and Stripe converted it at checkout.`;
};
